/**
 * printHelper.js
 *
 * Reliable A4 print utility.
 *
 * How it works:
 *   1. Finds the .print-document element in the current DOM.
 *   2. Deep-clones it into a brand-new <div id="print-output"> appended to <body>.
 *   3. Injects a tiny <style> that hides every other direct child of body during print.
 *   4. Calls window.print().
 *   5. Cleans up the clone and the injected style after the dialog closes.
 *
 * Why this works when CSS-only approaches fail:
 *   - The clone sits as a DIRECT child of <body>, so `body > *:not(#print-output)`
 *     reliably hides #root, Radix portals, toast containers, etc.
 *   - No position:fixed (clips to 1 page) or visibility hacks (ancestors steal space).
 *   - The content flows naturally, generating as many A4 pages as needed.
 */

const PRINT_STYLE = `
@media print {
  /* Hide everything except our print container */
  body > *:not(#print-output) {
    display: none !important;
  }

  /* A4 page setup */
  @page {
    size: A4 portrait;
    margin: 0;
  }

  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    width: 210mm !important;
    height: auto !important;
    overflow: visible !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  /* Container resets */
  #print-output {
    display: block !important;
    position: static !important;
    width: 210mm !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }

  /* The actual document */
  #print-output .print-document {
    display: block !important;
    position: static !important;
    width: 210mm !important;
    max-width: 210mm !important;
    min-height: 297mm !important;
    height: auto !important;
    padding: 12mm !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    background: #fff !important;
    color: #000 !important;
    box-shadow: none !important;
    border: none !important;
    overflow: visible !important;
  }

  /* Make sure everything inside prints */
  #print-output .print-document * {
    visibility: visible !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Page break control */
  #print-output table,
  #print-output tr {
    page-break-inside: avoid;
  }
}

/* Screen: the container is invisible — it's only for the printer */
@media screen {
  #print-output {
    position: absolute;
    left: -9999px;
    top: -9999px;
    width: 210mm;
    overflow: hidden;
    pointer-events: none;
  }
}
`;

/**
 * Prints the .print-document currently rendered in the DOM.
 * Returns a Promise that resolves after the print dialog closes.
 */
export function printDocument() {
  return new Promise((resolve) => {
    const source = document.querySelector(".print-document");
    if (!source) {
      console.warn("[printHelper] No .print-document element found in DOM");
      resolve();
      return;
    }

    // 1. Deep-clone the content
    const clone = source.cloneNode(true);

    // 2. Create a top-level container
    const container = document.createElement("div");
    container.id = "print-output";
    container.appendChild(clone);

    // 3. Inject print-specific styles
    const styleEl = document.createElement("style");
    styleEl.id = "print-output-style";
    styleEl.textContent = PRINT_STYLE;

    // 4. Attach to DOM
    document.head.appendChild(styleEl);
    document.body.appendChild(container);

    // 5. Small delay to let images and styles settle, then print
    requestAnimationFrame(() => {
      setTimeout(() => {
        window.print();

        // 6. Clean up after the print dialog closes
        container.remove();
        styleEl.remove();
        resolve();
      }, 150);
    });
  });
}
